import ServiceFeeNote from "@/components/ServiceFeeNote";
import useAutoRefresh from "@/hooks/useAutoRefresh";
import { useEffect, useState, useCallback } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Pill } from "@/components/perf";
import { toast } from "sonner";
import { Calendar, Clock, MapPin, User, CheckCircle2, Ticket, Loader2, ShoppingCart, FilterX, Phone, X, GraduationCap, ChevronDown } from "lucide-react";
import PageBackdrop from "@/components/PageBackdrop";
import ExpandableText from "@/components/ExpandableText";
import { Editable } from "@/context/ContentContext";
import { startCoinPayment } from "@/lib/coinpay";

const DRILL_COLORS = { Dinks: "#4ADE80", Drops: "#16A34A", Drives: "#A3E635", Resets: "#84CC16", Mixed: "#BEF264" };

export default function Book() {
  const [slots, setSlots] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [passes, setPasses] = useState([]);
  const [busyId, setBusyId] = useState(null);
  const [filters, setFilters] = useState({ coach: "", date: "", time: "", location: "" });
  const [bookingSlot, setBookingSlot] = useState(null);
  const { user } = useAuth();

  useEffect(() => {
    if (user?.phone) setContact((c) => (c.phone ? c : { ...c, phone: user.phone }));
  }, [user]);
  const [contact, setContact] = useState({ phone: "", contact_method: "text", note: "" });
  const [payMethod, setPayMethod] = useState("in_person");
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const [verifying, setVerifying] = useState(false);
  const { addPass, setOpen } = useCart();

  const load = useCallback(async () => {
    const [s, b, p] = await Promise.all([
      api.get("/sessions/slots"), api.get("/sessions/my-bookings"), api.get("/packages"),
    ]);
    setSlots(s.data); setBookings(b.data); setPasses(p.data.passes);
  }, []);

  const pollStatus = useCallback(async (sessionId, attempt = 0) => {
    if (attempt >= 6) { setVerifying(false); toast.error("Payment status check timed out. Check back shortly."); return; }
    try {
      const { data } = await api.get(`/payments/status/${sessionId}`);
      if (data.payment_status === "paid") {
        setVerifying(false);
        toast.success(data.kind === "session_booking"
          ? "Payment successful — session booked, pending coach approval!"
          : `Payment successful — ${data.name} activated!`);
        load();
        return;
      }
      if (data.status === "expired") { setVerifying(false); toast.error("Payment session expired."); return; }
      setTimeout(() => pollStatus(sessionId, attempt + 1), 2000);
    } catch {
      setVerifying(false); toast.error("Could not verify payment.");
    }
  }, [load]);

  useEffect(() => {
    const sid = params.get("session_id");
    if (sid) {
      setVerifying(true);
      pollStatus(sid);
      params.delete("session_id");
      setParams(params, { replace: true });
    }
  }, []); // eslint-disable-line

  useEffect(() => { load(); }, [load]);
  useAutoRefresh(load);

  const book = async () => {
    if (!contact.phone.trim()) return toast.error("Please enter a phone number so we can confirm your session");
    const slotId = bookingSlot.id;
    setBusyId(slotId);
    try {
      if (bookingSlot.price_coins && payMethod === "bullion") {
        const ok = await startCoinPayment(navigate, { kind: "slot_booking", item_id: slotId, payload: { ...contact } });
        if (!ok) setBusyId(null);
        return;
      }
      if (bookingSlot.price > 0 && payMethod === "stripe") {
        const { data } = await api.post(`/sessions/${slotId}/checkout`, { ...contact, origin_url: window.location.origin });
        if (data.booked) {
          toast.success(data.perk_used || "Session booked!");
          setBookingSlot(null);
          setContact({ phone: contact.phone, contact_method: "text", note: "" });
          await load();
          setBusyId(null);
          return;
        }
        window.location.href = data.url;
        return;
      }
      await api.post(`/sessions/${slotId}/book`, { ...contact, payment_method: "in_person" });
      toast.success("Session requested — pending coach approval!");
      setBookingSlot(null);
      setContact({ phone: contact.phone, contact_method: "text", note: "" });
      await load();
    }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); setBusyId(null); return; }
    setBusyId(null);
  };

  const cancel = async (slotId) => {
    setBusyId(slotId);
    try { await api.delete(`/sessions/${slotId}/book`); toast.success("Booking cancelled"); await load(); }
    finally { setBusyId(null); }
  };

  const buyPass = (p) => { addPass(p); setOpen(true); };

  const fmtDate = (d) => new Date(d + "T00:00:00").toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" });

  const opts = (key) => [...new Set(slots.map((s) => s[key]).filter(Boolean))].sort();
  const filtered = slots.filter((s) =>
    (!filters.coach || s.coach === filters.coach) &&
    (!filters.date || s.date === filters.date) &&
    (!filters.time || s.time === filters.time) &&
    (!filters.location || s.location === filters.location));
  const hasFilter = Object.values(filters).some(Boolean);

  return (
    <div>
      <PageBackdrop ck="backdrop.book" image="https://static.prod-images.emergentagent.com/jobs/ccb7baf7-4fe3-4351-b3c9-d8e5a28ac676/images/4ec804e9c900cfe1134bd941a4659a1133ea0ed1a89f910669eedf5f5fe67677.png" />
      <div className="relative z-10">
      {verifying && (
        <div className="fixed inset-0 z-[80] bg-black/80 flex items-center justify-center">
          <div className="bg-pb-surface border border-pb-cyan p-8 flex flex-col items-center gap-3">
            <Loader2 className="w-8 h-8 text-pb-cyan animate-spin" />
            <span className="font-display font-bold uppercase tracking-widest">Verifying Payment…</span>
          </div>
        </div>
      )}

      <div className="relative overflow-hidden border border-pb-border bg-pb-surface mb-8">
        <img src="https://static.prod-images.emergentagent.com/jobs/ccb7baf7-4fe3-4351-b3c9-d8e5a28ac676/images/b55667277d922b57ae30d0ac2a51aa585f3e26f69e15aa632b721c675456afc1.png"
          alt="" aria-hidden="true" data-testid="book-ball-bg"
          className="pointer-events-none select-none absolute -right-10 -bottom-16 w-72 sm:w-96 lg:w-[28rem] opacity-20 rotate-12" />
        <div className="absolute inset-0 bg-gradient-to-r from-pb-base via-pb-base/70 to-transparent" />
        <div className="relative px-5 py-8 md:px-8">
          <Editable ck="page.book.kicker" as="div" fallback="Drill Sessions · Reserve Your Court Time" className="text-xs font-bold uppercase tracking-[0.3em] text-pb-cyan mb-2" />
          <Editable ck="page.book.title" as="h1" fallback="Book Sessions" className="font-display font-black uppercase tracking-tighter text-4xl sm:text-5xl lg:text-6xl leading-none" />
          <Editable ck="page.book.subtitle" as="p" fallback="Reserve a spot in upcoming drill sessions — booking is free. Buy a session pass below to play." className="text-zinc-400 mt-3 max-w-2xl text-sm md:text-base" />
          <button data-testid="coaches-lounge-btn" onClick={() => navigate("/coaches-lounge")}
            className="mt-5 inline-flex items-center gap-2 border border-pb-cyan text-pb-cyan hover:bg-pb-cyan hover:text-black font-bold uppercase tracking-widest text-xs px-5 py-2.5 transition-all active:scale-95">
            <GraduationCap className="w-4 h-4" /> Coaches Lounge
          </button>
        </div>
      </div>

      {/* Session passes */}
      <h3 className="font-display font-bold text-xl uppercase mb-1 flex items-center gap-2"><Ticket className="w-5 h-5 text-pb-red" /> Drill Session Passes</h3>
      <p className="text-sm text-zinc-500 mb-5">Passes are sold separately from membership. Add to your cart and pay securely via Stripe.</p>
      <div className="grid md:grid-cols-3 gap-5 mb-12" data-testid="passes-grid">
        {passes.map((p, i) => (
          <div key={p.id} data-testid={`pass-${p.id}`} className={`bg-pb-surface border p-6 flex flex-col ${i === 1 ? "border-pb-red" : "border-pb-border"}`}>
            {i === 1 && <Pill color="#A3E635">Most Popular</Pill>}
            <div className="font-display font-black text-2xl uppercase mt-2">{p.name}</div>
            <div className="font-display font-black text-4xl text-pb-red my-3">${p.price}</div>
            <div className="flex-1 mb-6">
              <ExpandableText text={p.desc} lines={3} testid={`pass-desc-${p.id}`} className="text-sm text-zinc-400" />
            </div>
            <button data-testid={`buy-${p.id}`} onClick={() => buyPass(p)}
              className="w-full bg-pb-red text-black font-bold uppercase tracking-widest py-3 hover:bg-[#BEF264] active:scale-95 transition-all flex items-center justify-center gap-2">
              <ShoppingCart className="w-4 h-4" /> Add to Cart · ${p.price}
            </button>
            {p.price_coins && (
              <button data-testid={`coin-buy-pass-${p.id}`} onClick={() => startCoinPayment(navigate, { kind: "pass", item_id: p.id })}
                className="w-full mt-2 border border-purple-400/60 text-purple-300 hover:bg-purple-400/10 font-bold uppercase tracking-widest py-2.5 text-xs active:scale-95 transition-all">
                🪙 Pay with Coin · {p.price_coins} BLC
              </button>
            )}
          </div>
        ))}
      </div>

      {/* My bookings */}
      {bookings.length > 0 && (
        <div className="bg-pb-surface border border-pb-cyan/30 p-5 mb-8" data-testid="my-bookings">
          <div className="flex items-center gap-2 mb-3">
            <CheckCircle2 className="w-4 h-4 text-pb-cyan" />
            <span className="font-display font-bold text-lg uppercase">Your Upcoming Sessions</span>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {bookings.map((b, i) => (
              <div key={i} className="border border-pb-border p-3 flex items-center justify-between gap-2">
                <div>
                  <div className="font-semibold text-sm">{fmtDate(b.date)} · {b.time}</div>
                  <div className="text-xs text-zinc-500">{b.drill_type} · {b.coach} · {b.location}</div>
                </div>
                <div className="flex flex-col items-end gap-1">
                  <Pill color={DRILL_COLORS[b.drill_type] || "#A3E635"}>{b.drill_type}</Pill>
                  <Pill color={b.status === "confirmed" ? "#4ADE80" : b.status === "declined" ? "#F87171" : "#FACC15"}>
                    {b.status === "confirmed" ? "Confirmed" : b.status === "declined" ? "Declined" : "Pending Approval"}
                  </Pill>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Available slots */}
      <h3 className="font-display font-bold text-xl uppercase mb-4 flex items-center gap-2"><Calendar className="w-5 h-5 text-pb-red" /> Available Sessions</h3>

      {slots.length > 0 && (
        <div className="bg-pb-surface border border-pb-border p-4 mb-5" data-testid="session-filters">
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 items-end">
            {[["coach", "Coach", opts("coach"), null],
              ["date", "Date", opts("date"), fmtDate],
              ["time", "Time", opts("time"), null],
              ["location", "Location", opts("location"), null]].map(([key, label, options, fmt]) => (
              <div key={key}>
                <label className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 block mb-1.5">{label}</label>
                <select data-testid={`filter-${key}`} value={filters[key]}
                  onChange={(e) => setFilters({ ...filters, [key]: e.target.value })}
                  className="w-full bg-pb-elevated border border-pb-border focus:border-pb-red px-2.5 py-2 text-sm text-white focus:outline-none">
                  <option value="">All</option>
                  {options.map((o) => <option key={o} value={o}>{fmt ? fmt(o) : o}</option>)}
                </select>
              </div>
            ))}
            <button data-testid="filter-clear" onClick={() => setFilters({ coach: "", date: "", time: "", location: "" })}
              disabled={!hasFilter}
              className="flex items-center justify-center gap-2 border border-pb-border text-zinc-300 font-bold uppercase tracking-widest text-xs py-2.5 hover:border-pb-red hover:text-pb-red transition-colors disabled:opacity-40">
              <FilterX className="w-4 h-4" /> Clear
            </button>
          </div>
          {hasFilter && (
            <div className="text-xs text-zinc-500 mt-3" data-testid="filter-result-count">
              Showing {filtered.length} of {slots.length} sessions
            </div>
          )}
        </div>
      )}

      {hasFilter && filtered.length === 0 && (
        <div className="text-sm text-zinc-500 border border-pb-border bg-pb-surface p-5 mb-12" data-testid="filter-no-results">
          No sessions match those filters — try clearing one or more.
        </div>
      )}
      <div className="mb-12">
        <div data-testid="slots-scroll-window" className="max-h-[560px] overflow-y-auto border border-pb-border/60 bg-pb-base/30 p-4 space-y-7">
          <div data-testid="slots-grid" className="hidden" />
          {Object.entries(filtered.reduce((acc, s) => { (acc[s.date] = acc[s.date] || []).push(s); return acc; }, {})).map(([date, group]) => (
            <div key={date} data-testid={`day-group-${date}`}>
              <div className="flex items-center gap-3 mb-3">
                <span className="flex items-center gap-2 text-[11px] font-black uppercase tracking-[0.25em] text-pb-red">
                  <Calendar className="w-3.5 h-3.5" /> {fmtDate(date)}
                </span>
                <div className="flex-1 h-px bg-pb-border/70" />
                <span className="text-[10px] uppercase tracking-widest text-zinc-600">{group.length} session{group.length > 1 ? "s" : ""}</span>
              </div>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {group.map((s) => {
          const full = s.spots_remaining <= 0;
          return (
            <div key={s.id} data-testid={`slot-${s.id}`} className={`bg-pb-surface border p-5 transition-all hover:-translate-y-1 ${s.you_booked ? "border-pb-cyan" : full ? "border-pb-border opacity-70" : "border-pb-border hover:border-pb-red"}`}>
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Pill color={DRILL_COLORS[s.drill_type] || "#A3E635"}>{s.drill_type}</Pill>
                  <span data-testid={`slot-price-${s.id}`} className={`text-xs font-bold ${s.price > 0 ? "text-pb-cyan" : "text-zinc-500"}`}>
                    {s.price > 0 ? `$${s.price}` : "Free"}
                  </span>
                </div>
                <span className={`text-[10px] uppercase tracking-widest font-bold ${full ? "text-red-400" : "text-zinc-500"}`}>
                  {full ? "Full" : `${s.spots_remaining} spots left`}
                </span>
              </div>
              <div className="space-y-2 text-sm mb-4">
                <div className="flex items-center gap-2 text-zinc-200"><Calendar className="w-4 h-4 text-zinc-500" /> {fmtDate(s.date)}</div>
                <div className="flex items-center gap-2 text-zinc-200"><Clock className="w-4 h-4 text-zinc-500" /> {s.time}</div>
                <div className="flex items-center gap-2 text-zinc-400"><User className="w-4 h-4 text-zinc-500" /> {s.coach}</div>
                <div className="flex items-center gap-2 text-zinc-400"><MapPin className="w-4 h-4 text-zinc-500" /> {s.location}</div>
              </div>
              <div className="h-1.5 bg-pb-elevated mb-3">
                <div className="h-full bg-pb-red" style={{ width: `${(s.booked / s.capacity) * 100}%` }} />
              </div>
              {s.you_booked ? (
                <button data-testid={`cancel-${s.id}`} onClick={() => cancel(s.id)} disabled={busyId === s.id}
                  className="w-full border border-pb-cyan text-pb-cyan font-bold uppercase tracking-widest py-2 hover:bg-pb-cyan hover:text-black active:scale-95 transition-all disabled:opacity-50">
                  {busyId === s.id ? "…" : "Cancel Booking"}
                </button>
              ) : (
                <button data-testid={`book-${s.id}`} onClick={() => { setPayMethod("in_person"); setBookingSlot(s); }} disabled={busyId === s.id || full}
                  className="w-full bg-pb-red text-black font-bold uppercase tracking-widest py-2 hover:bg-[#BEF264] active:scale-95 transition-all disabled:opacity-40">
                  {busyId === s.id ? "…" : full ? "Full" : s.price > 0 ? `Book · $${s.price}` : "Book Free Spot"}
                </button>
              )}
            </div>
          );
        })}
              </div>
            </div>
          ))}
        </div>
        {filtered.length > 2 && (
          <div className="flex items-center justify-center gap-1.5 text-[11px] uppercase tracking-widest text-zinc-600 mt-2" data-testid="slots-scroll-hint">
            <ChevronDown className="w-3.5 h-3.5" /> Scroll for more sessions ({filtered.length} total)
          </div>
        )}
      </div>

      {bookingSlot && (
        <div className="fixed inset-0 z-[70] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setBookingSlot(null)}>
          <div className="bg-pb-surface border border-pb-red w-full max-w-md animate-fade-up" onClick={(e) => e.stopPropagation()} data-testid="booking-modal">
            <div className="flex items-center justify-between p-5 border-b border-pb-border">
              <div className="flex items-center gap-2">
                <Phone className="w-5 h-5 text-pb-red" />
                <h3 className="font-display font-black text-xl uppercase">Confirm Your Spot</h3>
              </div>
              <button data-testid="booking-modal-close" onClick={() => setBookingSlot(null)} className="text-zinc-500 hover:text-white"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-5 space-y-4">
              <div className="border border-pb-border bg-pb-elevated p-3 text-sm">
                <div className="font-semibold">{bookingSlot.drill_type} · {fmtDate(bookingSlot.date)} · {bookingSlot.time}</div>
                <div className="text-xs text-zinc-500 mt-0.5">{bookingSlot.coach} · {bookingSlot.location}{bookingSlot.price > 0 ? ` · $${bookingSlot.price}` : " · Free"}</div>
              </div>
              <p className="text-xs text-zinc-400">Leave your contact info — your booking is confirmed once the coach approves it.</p>
              <div>
                <label className="block text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-500 mb-2">Phone Number *</label>
                <input data-testid="booking-phone" value={contact.phone} autoFocus
                  onChange={(e) => setContact({ ...contact, phone: e.target.value })} placeholder="941-555-0000"
                  className="w-full bg-pb-elevated border border-pb-border px-3 py-2.5 text-white placeholder-zinc-600 focus:outline-none focus:border-pb-red" />
              </div>
              <div>
                <label className="block text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-500 mb-2">How should we reach you?</label>
                <div className="grid grid-cols-2 gap-2">
                  {[["text", "Text Me"], ["call", "Call Me"]].map(([v, l]) => (
                    <button key={v} type="button" data-testid={`booking-method-${v}`}
                      onClick={() => setContact({ ...contact, contact_method: v })}
                      className={`py-2.5 text-xs font-bold uppercase tracking-widest border transition-colors ${contact.contact_method === v ? "border-pb-red bg-pb-red/10 text-pb-red" : "border-pb-border text-zinc-500 hover:text-white"}`}>
                      {l}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="block text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-500 mb-2">Note (optional)</label>
                <textarea data-testid="booking-note" value={contact.note} rows={2}
                  onChange={(e) => setContact({ ...contact, note: e.target.value })} placeholder="Anything we should know?"
                  className="w-full bg-pb-elevated border border-pb-border px-3 py-2.5 text-white placeholder-zinc-600 focus:outline-none focus:border-pb-red resize-none" />
              </div>
              {bookingSlot.price > 0 && (
                <div>
                  <label className="block text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-500 mb-2">Payment · ${bookingSlot.price}</label>
                  <div className="grid grid-cols-3 gap-2">
                    {[["in_person", "Pay In Person"], ["stripe", "Pay Now (Card)"],
                      ...(bookingSlot.price_coins ? [["bullion", `Coin 🪙 ${bookingSlot.price_coins}`]] : [])].map(([v, l]) => (
                      <button key={v} type="button" data-testid={`pay-method-${v}`}
                        onClick={() => setPayMethod(v)}
                        className={`py-2.5 text-xs font-bold uppercase tracking-widest border transition-colors ${payMethod === v ? "border-pb-cyan bg-pb-cyan/10 text-pb-cyan" : "border-pb-border text-zinc-500 hover:text-white"}`}>
                        {l}
                      </button>
                    ))}
                  </div>
                  <p className="text-[11px] text-zinc-500 mt-2">
                    {payMethod === "stripe" ? "You'll be redirected to secure Stripe checkout. Booking stays pending until the coach approves."
                      : payMethod === "bullion" ? `You'll be taken to your Wallet to send ${bookingSlot.price_coins} BLC to the Bullpen. Booking stays pending until the coach approves.`
                      : "Bring payment to your session. Booking stays pending until the coach approves."}
                  </p>
                  {payMethod === "stripe" && <ServiceFeeNote className="mt-1" />}
                </div>
              )}
              <button data-testid="booking-submit" onClick={book} disabled={busyId === bookingSlot.id}
                className="w-full bg-pb-red text-black font-bold uppercase tracking-widest py-3 hover:bg-[#BEF264] active:scale-95 transition-all disabled:opacity-50">
                {busyId === bookingSlot.id ? "Booking…"
                  : bookingSlot.price > 0
                    ? (payMethod === "stripe" ? `Pay $${bookingSlot.price} & Request Spot`
                       : payMethod === "bullion" ? `Pay ${bookingSlot.price_coins} BLC & Request Spot`
                       : `Request Spot · $${bookingSlot.price} In Person`)
                    : "Request Free Spot"}
              </button>
            </div>
          </div>
        </div>
      )}
      </div>
    </div>
  );
}
